import type { BillingMode, ReleaseDate } from '../components/PackSizesPanel';

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MS_PER_DAY = 1000 * 60 * 60 * 24;

export function formatOrdinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  const suffix = n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
  return `${n}${suffix}`;
}

// "14 January 2027"
export function formatLongDate(date: Date): string {
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

// A pack frequency as far as billing dates care: unit ("Day" | "Week" | "Month") and label ("Every 3 months")
export interface FrequencyInterval {
  name: string;
  interval: string;
}

// Upcoming release dates strictly after `from`, soonest first
export function getUpcomingReleaseDates(releaseDates: ReleaseDate[], from: Date = new Date()): Date[] {
  const today = startOfDay(from);
  const years = [0, 1, 2].map(offset => today.getFullYear() + offset);
  return releaseDates.flatMap(({
    month,
    day
  }) => years.map(year => new Date(year, month - 1, day))).filter(date => date > today).sort((a, b) => a.getTime() - b.getTime());
}

export function getDaysUntil(date: Date, from: Date = new Date()): number {
  return Math.round((date.getTime() - startOfDay(from).getTime()) / MS_PER_DAY);
}

// Plain-sentence preview of what happens to a member joining today.
// Shopify always charges at sign-up. On release dates, a sign-up within the
// cutoff window counts as the next release, so their next charge is the one after.
export function describeJoiningToday(mode: BillingMode, fixedDay: number, releaseDates: ReleaseDate[], cutoffDays: number, frequencies: FrequencyInterval[], from: Date = new Date()): string {
  if (mode === 'fixedDay') return describeFixedDayJoin(fixedDay, frequencies, from);
  if (mode !== 'releaseDates') return 'Charged today, then every interval after.';
  const [nextRelease, releaseAfter] = getUpcomingReleaseDates(releaseDates, from);
  if (!nextRelease) return 'Add at least one release date to see when members are charged.';
  if (getDaysUntil(nextRelease, from) <= cutoffDays) return `Charged today, counts as the ${formatLongDate(nextRelease)} release. Next charge ${formatLongDate(releaseAfter)}.`;
  return `Charged today. Next charge ${formatLongDate(nextRelease)}.`;
}

function describeFixedDayJoin(fixedDay: number, frequencies: FrequencyInterval[], from: Date): string {
  const shortest = getShortestFrequency(frequencies);
  if (!shortest) return 'Charged today. Add a frequency to see when the next charge lands.';
  const today = startOfDay(from);
  // Today plus one interval, then onto the chosen day in that month
  const cycleEnd = shortest.unit === 'Month' ? new Date(today.getFullYear(), today.getMonth() + shortest.count, today.getDate()) : new Date(today.getFullYear(), today.getMonth(), today.getDate() + shortest.count * (shortest.unit === 'Week' ? 7 : 1));
  let nextCharge = new Date(cycleEnd.getFullYear(), cycleEnd.getMonth(), fixedDay);
  if (nextCharge <= today) nextCharge = new Date(cycleEnd.getFullYear(), cycleEnd.getMonth() + 1, fixedDay);
  const adjective = getFrequencyAdjective(shortest.unit, shortest.count);
  const article = /^[aeiou]/i.test(adjective) ? 'An' : 'A';
  return `Charged today. ${article} ${adjective} member's next charge is ${formatLongDate(nextCharge)}.`;
}

function getShortestFrequency(frequencies: FrequencyInterval[]) {
  const parsed = frequencies.map(freq => {
    const count = parseInt(freq.interval.replace(/\D+/g, ''), 10);
    const unit = freq.name as 'Day' | 'Week' | 'Month';
    if (!['Day', 'Week', 'Month'].includes(unit) || !Number.isFinite(count) || count <= 0) return null;
    const approxDays = unit === 'Month' ? count * 30.44 : unit === 'Week' ? count * 7 : count;
    return {
      unit,
      count,
      approxDays
    };
  }).filter((freq): freq is NonNullable<typeof freq> => freq !== null);
  return parsed.sort((a, b) => a.approxDays - b.approxDays)[0] ?? null;
}

function getFrequencyAdjective(unit: 'Day' | 'Week' | 'Month', count: number): string {
  if (unit === 'Month') {
    const named: Record<number, string> = {
      1: 'monthly',
      2: 'bi-monthly',
      3: 'quarterly',
      6: 'half-yearly',
      12: 'annual'
    };
    return named[count] ?? `${count}-month`;
  }
  if (unit === 'Week') return count === 1 ? 'weekly' : count === 2 ? 'fortnightly' : `${count}-week`;
  return `${count}-day`;
}

// Badge for the collapsed frequency row; null for Rolling
export function getBillingModeBadge(mode: BillingMode, fixedDay: number, releaseDates: ReleaseDate[]): string | null {
  if (mode === 'fixedDay') return `Fixed day: ${formatOrdinal(fixedDay)}`;
  if (mode === 'releaseDates') return `Releases: ${formatReleaseDates(releaseDates)}`;
  return null;
}

// "14 Jan, 14 Jul", in calendar order
export function formatReleaseDates(releaseDates: ReleaseDate[]): string {
  return [...releaseDates].sort((a, b) => a.month - b.month || a.day - b.day).map(date => `${date.day} ${MONTHS_SHORT[date.month - 1]}`).join(', ');
}
